import { Dialog, DialogTitle, DialogContent, Box, IconButton, Button, Divider } from "@mui/material";
import Close from "@mui/icons-material/Close";
import WarningOutlined from "@mui/icons-material/WarningOutlined";
import { FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { ValidationMessage } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { Link, useParams, useLocation } from "react-router";
import { resolveUrlForProperty } from "src/utils/useQuestionnaireNavigation.ts";

type ConfirmWarningModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (force: boolean) => void;
    validationMessages: ValidationMessage[];
};

export const ConfirmWarningModal = (props: ConfirmWarningModalProps) => {
    const { isOpen, onClose, onConfirm, validationMessages } = props;
    const { t } = useTranslation();
    const params = useParams();
    const location = useLocation();

    const warningMessages = validationMessages.filter(
        (msg) => msg.severity === "warning" || msg.severity === "warning_with_block",
    );

    return (
        <Dialog maxWidth="sm" fullWidth open={isOpen} onClose={onClose}>
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3" sx={{ fontWeight: 700 }}>
                        {t(
                            "validation.confirm_warning.modal_title",
                            "Are you sure you want to validate your questionnaire?",
                        )}
                    </Typography>
                    <IconButton onClick={onClose} edge="end" size="small" aria-label="close">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ p: 3 }}>
                <Box display="flex" flexDirection="column" gap={4}>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                            <Box
                                display="flex"
                                alignItems="center"
                                gap={0.5}
                                sx={{
                                    backgroundColor: "#FFF7E9",
                                    borderRadius: "4px",
                                    px: 1,
                                    py: 0.25,
                                    height: "26px",
                                    width: "fit-content",
                                }}
                            >
                                <WarningOutlined sx={{ fontSize: 16, color: "#BB7600" }} />
                                <Typography
                                    variant="body-medium"
                                    sx={{ fontWeight: 700, fontSize: 14, color: "#BB7600" }}
                                >
                                    {t("validation.errors.warning_badge", "Alerts")}
                                </Typography>
                            </Box>
                            <Typography variant="body-medium" sx={{ fontWeight: 700, fontSize: 14 }}>
                                {t(
                                    "validation.errors.warning_title",
                                    "The following issues have been identified in your questionnaire without blocking its validation",
                                )}
                                :
                            </Typography>
                        </Box>

                        {warningMessages.map((validationMessage, index) => {
                            const propertyName = validationMessage.hint?.propertyName;
                            const questionnaireLink = resolveUrlForProperty(
                                params,
                                validationMessage,
                                location.pathname,
                            );

                            return (
                                <Box
                                    key={`warning-${index}`}
                                    display="flex"
                                    alignItems="center"
                                    sx={{
                                        borderLeft: "2px solid #BB7600",
                                        pl: 2,
                                    }}
                                >
                                    <Box flex={1} display="flex" flexDirection="column">
                                        {propertyName && (
                                            <Typography variant="body-medium" sx={{ fontWeight: 600, fontSize: 14 }}>
                                                {propertyName}
                                            </Typography>
                                        )}
                                        <Typography variant="body-medium" sx={{ fontWeight: 300, fontSize: 14 }}>
                                            {validationMessage.message}
                                        </Typography>
                                    </Box>
                                    {questionnaireLink !== null && (
                                        <Link to={questionnaireLink}>
                                            <FoodsIconButton
                                                size={"xs"}
                                                icon={"diagnostic"}
                                                withBackground
                                                tooltip={{
                                                    text: t(
                                                        "validation.errors.correct_tooltip",
                                                        "Click to correct this in the questionnaire.",
                                                    ),
                                                }}
                                            />
                                        </Link>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    <Divider />

                    <Typography variant="body-medium" sx={{ fontSize: "14px" }}>
                        {t(
                            "validation.confirm_warning.info_text",
                            "If you validate, the questionnaire will be locked. However, you can unlock it if necessary.",
                        )}
                    </Typography>
                </Box>
            </DialogContent>

            <Divider />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    px: 4,
                    pt: 4,
                    pb: 4.5,
                }}
            >
                <Button onClick={() => onConfirm(true)} variant="primary" fullWidth>
                    {t("validation.confirm_warning.button_confirm", "Yes, validate")}
                </Button>
                <Button onClick={onClose} variant="ternary" fullWidth>
                    {t("validation.confirm_warning.button_cancel", "No, return to questionnaire")}
                </Button>
            </Box>
        </Dialog>
    );
};
